import React, { useEffect } from 'react';
import { injectIntl, WrappedComponentProps } from '@kyruus/intl';
import { ProviderMatchProviderV9, PMCConfig } from '@kyruus/types';
import { isVirtualProvider } from '@kyruus/provider-components';

import {
  CardContentText,
  CardContentContainer,
  CardContentHeader,
  ProviderInformationCard
} from './styles';

import messages from '../../lib/messages';
import { isSummaryDataConfigured } from '../../lib/utils';
import { withErrorBoundary } from '../ErrorFallback/with-error-boundary';

interface TelehealthProps {
  provider: ProviderMatchProviderV9;
  config: PMCConfig;
  bookOnlineUrl?: string;
  hasAvailability: boolean;
  shouldHidePageAnchors: boolean;
  onBook: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => void;
  shouldSkipRender: (component: string) => void;
}

const Telehealth = ({
  intl,
  provider,
  config,
  shouldSkipRender
}: TelehealthProps & WrappedComponentProps): React.ReactElement | null => {
  const isTelehealthIgnored = (config.provider_v9?.summary_data || []).find(
    (s: any) =>
      s.field === 'telehealth' &&
      Array.isArray(s.ignore) &&
      s.ignore.includes('true')
  );

  const skipRendering =
    !isVirtualProvider(provider) ||
    !isSummaryDataConfigured('telehealth', config) ||
    typeof provider.telehealth !== 'boolean' ||
    provider.telehealth === false ||
    isTelehealthIgnored;

  useEffect(() => {
    if (skipRendering) {
      // callback to the parent component to skip rendering
      shouldSkipRender('telehealth');
    }
  }, [skipRendering]);

  // do not render if data not present or if provider doesn't offer video visits
  if (skipRendering) {
    return null;
  }

  return (
    <ProviderInformationCard data-testid="info-card-telehealth">
      <CardContentContainer>
        <CardContentHeader>
          {intl.formatMessage(messages.telehealthHeader)}
        </CardContentHeader>
        <CardContentText>
          {intl.formatMessage(messages.telehealthText)}
        </CardContentText>
      </CardContentContainer>
    </ProviderInformationCard>
  );
};

export default withErrorBoundary(injectIntl(Telehealth));
